import React from 'react';
import { LanguageContext } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import reg from '../images/regestrieren-grips.svg';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import EditableText from './EditableText';

const BLUE = '#144378';

const timelineIconStyle = { background: BLUE, color: BLUE };

const timelineBtnStyle: React.CSSProperties = {
    width: '100%',
    color: BLUE,
    backgroundColor: 'transparent',
    border: `2px solid ${BLUE}`,
    fontSize: '1.1rem',
    padding: '0.6rem 1.2rem',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s, color 0.2s',
};

const timelineBtnHoverStyle: React.CSSProperties = {
    backgroundColor: BLUE,
    color: '#fff',
};

/** Formats a date offset from today in the given locale. */
const formatDate = (daysOffset: number, days: string[], locale: string): string => {
    const d = new Date();
    d.setDate(d.getDate() + daysOffset);
    const dayName = days[d.getDay()];
    const dayOfMonth = d.getDate().toString().padStart(2, '0');
    const monthUpper = d.toLocaleString(locale, { month: 'long' }).toUpperCase();
    return `${dayName}, ${dayOfMonth}. ${monthUpper} ${d.getFullYear()}`;
};

const TEXT_CLS = 'text-grips fs-6 fs-md-5 fs-lg-5';

const HomeSection3: React.FC = () => {
    const { language } = React.useContext(LanguageContext);
    const navigate = useNavigate();

    const isDE = language === 'de';
    const days = isDE
        ? ['SONNTAG', 'MONTAG', 'DIENSTAG', 'MITTWOCH', 'DONNERSTAG', 'FREITAG', 'SAMSTAG']
        : ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    const locale = isDE ? 'de-DE' : 'en-US';

    const dates = [0, 4, 6, 10, 34].map((offset) => formatDate(offset, days, locale));

    // EditableText IDs differ per language (DE: 29-48, EN: 49-68)
    const idOffset = isDE ? 29 : 49;

    const [hovered, setHovered] = React.useState(false);

    const startBtnLabel = isDE ? 'Termin vereinbaren' : 'Book a meeting';
    const learnMoreLabel = isDE ? 'Erfahren Sie mehr' : 'Learn more';

    return (
        <div className='my-5'>
            <div>
                <VerticalTimeline lineColor={BLUE}>
                    {/* --- Step 1 --- */}
                    <VerticalTimelineElement
                        date={dates[0]}
                        iconStyle={timelineIconStyle}
                        icon={<img src={reg} alt="Event 1" />}
                    >
                        <h4 className='h4-timeline' id={String(idOffset)}>
                            <EditableText id={String(idOffset)} />
                        </h4>
                        <div className={TEXT_CLS} id={String(idOffset + 1)}>
                            <EditableText id={String(idOffset + 1)} isTL />
                        </div>
                        <ul className={TEXT_CLS}>
                            {[2, 3, 4, 5].map((i) => (
                                <li key={i}>
                                    <div className={TEXT_CLS} id={String(idOffset + i)}>
                                        <EditableText id={String(idOffset + i)} isTL />
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <button
                            type="button"
                            style={hovered ? { ...timelineBtnStyle, ...timelineBtnHoverStyle } : timelineBtnStyle}
                            onMouseEnter={() => setHovered(true)}
                            onMouseLeave={() => setHovered(false)}
                            onClick={() => navigate('/setup')}
                        >
                            {startBtnLabel}
                        </button>
                    </VerticalTimelineElement>

                    {/* --- Step 2 --- */}
                    <VerticalTimelineElement
                        date={dates[1]}
                        iconStyle={timelineIconStyle}
                        icon={<img src={reg} alt="Event 2" />}
                    >
                        <h4 className='h4-timeline' id={String(idOffset + 6)}>
                            <EditableText id={String(idOffset + 6)} />
                        </h4>
                        <div className={TEXT_CLS} id={String(idOffset + 7)}>
                            <EditableText id={String(idOffset + 7)} isTL />
                        </div>
                    </VerticalTimelineElement>

                    {/* --- Step 3 --- */}
                    <VerticalTimelineElement
                        date={dates[2]}
                        iconStyle={timelineIconStyle}
                        icon={<img src={reg} alt="Event 3" />}
                    >
                        <h4 className='h4-timeline' id={String(idOffset + 8)}>
                            <EditableText id={String(idOffset + 8)} />
                        </h4>
                        <div className={TEXT_CLS} id={String(idOffset + 9)}>
                            <EditableText id={String(idOffset + 9)} isTL />
                        </div>
                        <ul>
                            {[10, 11, 12, 13, 14].map((i) => (
                                <li key={i}>
                                    <div className={TEXT_CLS} id={String(idOffset + i)}>
                                        <EditableText id={String(idOffset + i)} isTL />
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className={TEXT_CLS} id={String(idOffset + 15)}>
                            <EditableText id={String(idOffset + 15)} isTL />
                        </div>
                    </VerticalTimelineElement>

                    {/* --- Step 4 --- */}
                    <VerticalTimelineElement
                        date={dates[3]}
                        iconStyle={timelineIconStyle}
                        icon={<img src={reg} alt="Event 4" />}
                    >
                        <h4 className='h4-timeline' id={String(idOffset + 16)}>
                            <EditableText id={String(idOffset + 16)} />
                        </h4>
                        <div className={TEXT_CLS} id={String(idOffset + 17)}>
                            <EditableText id={String(idOffset + 17)} isTL />
                        </div>
                    </VerticalTimelineElement>

                    {/* --- Step 5 --- */}
                    <VerticalTimelineElement
                        date={dates[4]}
                        iconStyle={timelineIconStyle}
                        icon={<img src={reg} alt="Event 5" />}
                    >
                        <h4 className='h4-timeline' id={String(idOffset + 18)}>
                            <EditableText id={String(idOffset + 18)} />
                        </h4>
                        <div className={TEXT_CLS} id={String(idOffset + 19)}>
                            <EditableText id={String(idOffset + 19)} isTL />
                        </div>
                    </VerticalTimelineElement>
                </VerticalTimeline>

                <br />
                <br />

                <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                    <button type="button" className="btn btn-outline-danger btn-lg" onClick={() => navigate('/merkmale')}>
                        {learnMoreLabel}
                    </button>
                    <button type="button" className="btn btn-outline-danger btn-lg" onClick={() => navigate('/setup')}>
                        {startBtnLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HomeSection3;